import * as React from 'react';
import { StyleSheet, TextStyle, Text } from 'react-native';

import { localeTexts } from '../../Locales';
import HighlightedText from '../common/HighlightedText';
import { font } from '../../style/text';
import color from '../../style/color';

const PASSWORD_MIN_CHARACTERS = 10;
const PASSWORD_MAX_CHARACTERS = 50;
const PASSWORD_MIN_LOWER_CASE = 1;
const PASSWORD_MIN_UPPER_CASE = 1;
const PASSWORD_MIN_NUMBERS = 1;
const texts = localeTexts.auth.signUp.passwordRequirements;

const PasswordRequirements = ({
    passwordRequirementsChecks,
    style,
    isInline,
}: {
    passwordRequirementsChecks: PasswordRequirementsChecks;
    style?: TextStyle;
    isInline?: boolean;
}) => {
    const Requirements = () => (
        <>
            <PasswordRequirementLine isChecked={passwordRequirementsChecks.minCharacters}>
                {texts.minCharacters({ minCharacters: PASSWORD_MIN_CHARACTERS })}
            </PasswordRequirementLine>
            <PasswordRequirementLine isChecked={passwordRequirementsChecks.minLowerCase}>
                {texts.minLowerCase({ minLowerCase: PASSWORD_MIN_LOWER_CASE })}
            </PasswordRequirementLine>
            <PasswordRequirementLine isChecked={passwordRequirementsChecks.minUpperCase}>
                {texts.minUpperCase({ minUpperCase: PASSWORD_MIN_UPPER_CASE })}
            </PasswordRequirementLine>
            <PasswordRequirementLine isChecked={passwordRequirementsChecks.minNumbers}>
                {texts.minNumbers({ minNumbers: PASSWORD_MIN_NUMBERS })}
            </PasswordRequirementLine>
        </>
    );
    if (isInline)
        return (
            <Text style={style}>
                <Requirements />
            </Text>
        );
    else
        return (
            <Text style={[styles.textPasswordRequirements, style]}>
                <Requirements />
            </Text>
        );
};

export default PasswordRequirements;

// Each requirement turns from semibold grey to semibold black once it is met. The unmet weight
// stays the same grey as the sentence around it: highlighting it in a *lighter* grey, as it used to
// be, made the words that matter the faintest ones on the screen.
const PasswordRequirementLine = ({ isChecked, children }: { isChecked: boolean; children: string }) => (
    <HighlightedText
        style={styles.textPasswordRequirement}
        highlightedStyle={{
            color: isChecked ? color.black : color.boulder,
            fontFamily: font.instrumentSansSemiBold,
        }}>
        {children}
    </HighlightedText>
);

export interface PasswordRequirementsChecks {
    minCharacters: boolean;
    minLowerCase: boolean;
    minUpperCase: boolean;
    minNumbers: boolean;
    all: boolean;
}

export function checkPasswordRequirements(password: string): PasswordRequirementsChecks {
    const minCharacters = password.length >= PASSWORD_MIN_CHARACTERS && password.length <= PASSWORD_MAX_CHARACTERS;
    const minLowerCase = password.length - password.replace(/[a-z]/g, '').length >= PASSWORD_MIN_LOWER_CASE;
    const minUpperCase = password.length - password.replace(/[A-Z]/g, '').length >= PASSWORD_MIN_UPPER_CASE;
    const minNumbers = password.length - password.replace(/[0-9]/g, '').length >= PASSWORD_MIN_NUMBERS;
    const checks: PasswordRequirementsChecks = {
        minCharacters,
        minLowerCase,
        minUpperCase,
        minNumbers,
        all: minCharacters && minLowerCase && minUpperCase && minNumbers,
    };
    return checks;
}

export function usePasswordRequirementsChecks(password: string): PasswordRequirementsChecks {
    const [passwordRequirementsChecks, setPasswordRequirementsChecks] = React.useState<PasswordRequirementsChecks>(
        checkPasswordRequirements(password)
    );
    React.useEffect(() => {
        setPasswordRequirementsChecks(checkPasswordRequirements(password));
    }, [password]);
    return passwordRequirementsChecks;
}

const styles = StyleSheet.create({
    // One text block, not a wrapping row of four: as separate flex items the segments were laid out
    // side by side, so the space between them collapsed at every wrap point and the two lines sat
    // almost on top of each other. As nested text they flow as a single sentence.
    //
    // Type follows the design (12px, `#747474`); the line height is the one deliberate departure —
    // the design's 14.4px is a single-line value, and this sentence always wraps to two lines in a
    // 384px column, where 1.2 leading is exactly what read as cramped. 18px is the 1.5 ratio
    // recommended for wrapped body text.
    textPasswordRequirements: {
        lineHeight: 18,
    },
    textPasswordRequirement: {
        color: color.boulder,
        fontFamily: font.instrumentSansMedium,
        fontSize: 12,
        lineHeight: 18,
    },
});
